// frontend/smartdocs-admin/src/components/common/ColorPicker.tsx
import { useState, useRef, useEffect } from 'react';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { Palette, X } from 'lucide-react';

// Predefined color presets
const DEFAULT_PRESETS = [
  '#bf1650', '#e53e3e', '#dd6b20', '#d69e2e', '#38a169', '#3182ce', '#805ad5', '#d53f8c',
  '#f6ad55', '#48bb78', '#4299e1', '#9f7aea', '#ed64a6', '#ecc94b', '#68d391', '#63b3ed',
];

interface ColorPickerProps {
  id: string;
  label: string;
  description?: string;
  value: string;
  onChange: (color: string) => void;
  presets?: string[];
  disabled?: boolean;
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  id,
  label,
  description,
  value,
  onChange,
  presets = DEFAULT_PRESETS,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [pickerRef]);

  const handleColorChange = (color: string) => {
    onChange(color);
  };

  const handleReset = () => {
    onChange(''); // Or a default color if preferred
  };

  return (
    <div className="py-3 relative" ref={pickerRef}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-900 dark:text-gray-100">
        {label}
      </label>
      {description && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-2">
          {description}
        </p>
      )}
      <div className="flex items-center space-x-2 mt-2">
        <div
          className={`w-12 h-12 rounded-md border border-gray-300 shadow-sm dark:border-gray-600 cursor-pointer flex items-center justify-center ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ backgroundColor: value || 'transparent' }}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          aria-label="Open color picker"
        >
          {!value && <Palette size={20} className="text-gray-400 dark:text-gray-500" />}
        </div>
        <HexColorInput
          id={id}
          color={value}
          onChange={handleColorChange}
          className={`flex-grow p-2 border border-gray-300 rounded-md shadow-sm text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          alpha
          disabled={disabled}
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={handleReset}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-md transition-colors"
            aria-label="Reset color"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-10 mt-2 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <HexColorPicker color={value} onChange={handleColorChange} className="w-full" />
          <div className="flex flex-wrap gap-2 mt-4">
            {presets.map((color, index) => (
              <div
                key={index}
                className="w-6 h-6 rounded-full cursor-pointer border border-gray-200 dark:border-gray-700"
                style={{ backgroundColor: color }}
                onClick={() => handleColorChange(color)}
                aria-label={`Select color ${color}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
